import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '@resource-kit/ui';
import { JsonPanel } from '@components/ui/JsonPanel';

export interface RawDataDisclosureProps {
  /** The verbatim value — an array/object, or the JSON string it arrived as. */
  value: unknown;
  /** Accessible name for the revealed panel's table. */
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
}

/**
 * The escape hatch beside a summarising view: a toggle that reveals the
 * verbatim payload the summary was derived from.
 *
 * It exists because a table of the four fields an operator reads is the right
 * DEFAULT and the wrong FLOOR. A Shopify line item carries `properties`,
 * `variant_id`, `requires_shipping`, `tax_lines` — none of which belongs in a
 * packing table, all of which someone eventually has to see to answer "why did
 * this land on this date". Before this, the choice was between a table that
 * hid them and a raw dump that buried the four that matter.
 *
 * CLOSED by default and rendered only when opened, which is the whole point of
 * the split: the table is the answer, the blob is the appeal. `JsonPanel`
 * supplies the tree, the copy button and the fullscreen dialog, so this adds
 * only the disclosure.
 */
export function RawDataDisclosure({
  value,
  ariaLabel,
  className,
  children,
}: RawDataDisclosureProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className={cn('mt-2', className)}>
      <button
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
      >
        <ChevronRight className={cn('h-3 w-3 transition-transform', open && 'rotate-90')} />
        {children ?? t('lineItem.rawData')}
      </button>
      {open && (
        <div className="mt-2">
          <JsonPanel value={value} ariaLabel={ariaLabel ?? t('lineItem.rawData')} />
        </div>
      )}
    </div>
  );
}
